import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { listUsers } from '../api/users';
import FormattedDate from '../components/FormattedDate';
import Button from '../components/common/Button';
import BookPage from '../components/common/BookPage';
import Card from '../components/common/Card';

const PAGE_SIZE = 10;

const AdminUsersPage: React.FC = () => {
  const { user, token } = useAuth();
  const [offset, setOffset] = useState(0);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', offset, token],
    queryFn: () => listUsers(PAGE_SIZE, offset, token!),
    enabled: Boolean(token) && user?.user_role === 'admin',
    placeholderData: (previousData) => previousData,
  });

  if (!user || user.user_role !== 'admin') {
    return (
      <div className="min-h-screen pt-16 md:pt-20">
        <section className="mx-auto max-w-3xl px-4 py-16">
          <Card className="text-center">
            <h2 className="font-serif text-3xl text-[#2c201b]">Private directory.</h2>
            <p className="mt-3 text-[#5b5048]">You must be an admin to view all users.</p>
          </Card>
        </section>
      </div>
    );
  }

  const users = usersQuery.data ?? [];
  const errorMessage = usersQuery.error instanceof Error ? usersQuery.error.message : '';
  const hasMore = users.length === PAGE_SIZE;

  return (
    <BookPage
      eyebrow="Admin"
      title="Manage users."
      intro="Review account activity, roles, and profile details without leaving the admin flow."
      bodyClassName="space-y-6"
      maxWidthClassName="max-w-6xl"
    >
      {errorMessage && <div className="rounded-[0.8rem] border border-red-200 bg-red-50/90 p-3 text-sm text-red-700">{errorMessage}</div>}

      {usersQuery.isLoading ? (
        <div className="py-12 text-center text-[#5b5048]">Loading users…</div>
      ) : users.length === 0 ? (
        <div className="py-12 text-center text-[#5b5048]">No users found.</div>
      ) : (
        <div className="border-y border-[color:var(--wf-border)]">
          {users.map((adminUser, index) => (
            <article
              key={adminUser.id}
              className={`py-6 sm:py-7 ${index !== users.length - 1 ? 'border-b border-[color:var(--wf-border)]/80' : ''}`}
            >
              <Link to={`/admin/users/${adminUser.id}`} className="font-serif text-2xl text-[color:var(--wf-ink)] transition-colors hover:text-[color:var(--wf-plum)]">
                {adminUser.first_name} {adminUser.last_name}
              </Link>
              <dl className="mt-4 grid gap-3 text-sm text-[color:var(--wf-ink-soft)] sm:grid-cols-2 xl:grid-cols-4">
                <div>
                  <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Email</dt>
                  <dd className="mt-1 break-all">{adminUser.email}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Role</dt>
                  <dd className="mt-1">{adminUser.user_role}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Created</dt>
                  <dd className="mt-1"><FormattedDate date={adminUser.created_at} /></dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-[0.18em] text-[color:var(--wf-brass)]">Updated</dt>
                  <dd className="mt-1"><FormattedDate date={adminUser.updated_at} /></dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-3 border-t border-[#8f7e69]/15 pt-6">
        <Button type="button" variant="secondary" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}>
          Previous
        </Button>
        <Button type="button" variant="secondary" onClick={() => setOffset(offset + PAGE_SIZE)} disabled={!hasMore}>
          Next
        </Button>
      </div>
    </BookPage>
  );
};

export default AdminUsersPage;
